import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { FacilityDetailsInterface } from '../../interface/station-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import chargingStation from '../../utils/api/station';
import BatteryLoader from '../battery-loader/BatteryLoader';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  currentData?: any;
}

/** Validate lat lng */
const validatePosition = (lat: any, lng: any): google.maps.LatLngLiteral | null => {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Station side panel
 * @param properties
 * @returns
 */
function FacilityViewSidePanel(properties: StationSidePanelProperties) {
  const branding = useAppBranding();
  const { fetchIdFrom, id } = properties;
  const { t } = useTranslation('chargingSession');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoaded } = useGoogleMaps();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [searchParameters] = useSearchParams();
  const [facilityData, setFacilityData] = useState<FacilityDetailsInterface | null>(null);

  /**
   * Function to fetch station details
   * @param facilityId
   */
  const handleFetchFacilityDetails = async (facilityId: string) => {
    chargingStation
      .fetchFacilityDetails(facilityId)
      .then((response) => {
        setFacilityData(response?.data?.[0]);
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the open station management
   */
  const handleOpenStationManagement = (stationId: string, stationName: string) => {
    navigate(
      `/ev-admin/facility-management/station/${stationId}/station-dashboard?searchBy=${stationName}`,
    );
  };

  useEffect(() => {
    setIsLoader(true);
    const facilityId = fetchIdFrom === 'queryParams' ? searchParameters.get('facilityId') : id;

    if (!facilityId) {
      setFacilityData(null);
      setIsLoader(false);
      return;
    }

    handleFetchFacilityDetails(facilityId);
  }, [searchParameters, id, fetchIdFrom]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="facility-side-panel-wrap">
      <div className="facility-side-panel-top">
        <div className="facility-details-container">
          <div className="facility-details-header">
            <p className="title">{t('facilityDetails')}</p>
          </div>
          <div className="station-side-panel-container">
            <div className="station-data-wrapper">
              <div className="station-content-wrap">
                <div className="station-content-header">
                  <h2 className="station-name">{facilityData?.facilityTagName ?? 'N/A'}</h2>
                  <p className="station-address">{facilityData?.address ?? 'N/A'}</p>
                </div>
                <div className="divider" />
                <div className="station-content">
                  {facilityData?.chargingStationsData?.map((station) => {
                    const validPosition = validatePosition(station.latitude, station.longitude);
                    if (!isLoaded || !validPosition) return null;
                    return (
                      <div className="station-content-container" key={station._id}>
                        <div className="map-wrap">
                          <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={{
                              lat: Number(station.latitude),
                              lng: Number(station.longitude),
                            }}
                            zoom={12}>
                            {validPosition && (
                              <Marker
                                position={validPosition}
                                icon={{
                                  url: branding.icons.stationLocator,
                                  scaledSize: new window.google.maps.Size(44, 44),
                                }}
                              />
                            )}
                          </GoogleMap>
                        </div>
                        <div key={station._id} className="station-content-item-wrapper">
                          <div className="station-content-item-container">
                            <h3
                              role="presentation"
                              className="station-content-item-title"
                              onClick={() =>
                                handleOpenStationManagement(station._id, station.name)
                              }>
                              {station.name}
                            </h3>
                            <p className="station-content-item-address">{station.address}</p>
                            <div className="station-status">
                              <div className={`status ${station.isAvailable ? 'open' : 'closed'} `}>
                                {station.isAvailable ? t('open') : t('closed')}
                              </div>
                              <span className="time">
                                <ClockIcon className="clock-icon" />
                                {FormatUtcString(ConvertUTCtoHHMM(station?.opensAt || ''))}-
                                {FormatUtcString(ConvertUTCtoHHMM(station?.closesAt || ''))}
                              </span>
                              <NewTabIcon
                                className="open-icon"
                                onClick={() =>
                                  handleOpenStationManagement(station._id, station.name)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FacilityViewSidePanel;
